import './CollapsibleContent.scss';

import React, { useEffect, useRef, useState } from 'react';

import bem from '../../utils/bem';

const [block, element] = bem('collapsible-content');

export type CollapsibleContentRenderProps = {
  isOverflowing: boolean;
  isExpanded: boolean;
};

type ChildrenRenderFn = (props: CollapsibleContentRenderProps) => React.ReactNode;

export type CollapsibleContentProps = {
  children: React.ReactNode | ChildrenRenderFn;
  collapsedMaxHeight: number;
  expandLabel?: string;
  collapseLabel?: string;
  footer?: (props: CollapsibleContentRenderProps) => React.ReactNode;
  forceOverflowing?: boolean;
  dependencies?: unknown[];
  className?: string;
  testId?: string;
};

const CollapsibleContent: React.FC<CollapsibleContentProps> = ({
  children,
  collapsedMaxHeight,
  expandLabel = 'View all',
  collapseLabel = 'View less',
  footer,
  forceOverflowing,
  dependencies = [],
  className,
  testId,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkOverflow = (): void => {
      setIsOverflowing(el.scrollHeight > collapsedMaxHeight);
    };

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    checkOverflow();

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsedMaxHeight, ...dependencies]);

  const effectiveOverflowing = isOverflowing || !!forceOverflowing;
  const isCollapsed = effectiveOverflowing && !isExpanded;
  const renderProps: CollapsibleContentRenderProps = { isOverflowing: effectiveOverflowing, isExpanded };

  return (
    <div
      className={`${block(isCollapsed ? 'collapsed' : undefined)}${className ? ` ${className}` : ''}`}
      data-testid={testId}
    >
      <div
        ref={contentRef}
        className={element('body')}
        style={isCollapsed ? { maxHeight: `${collapsedMaxHeight}px` } : undefined}
      >
        {typeof children === 'function' ? (children as ChildrenRenderFn)(renderProps) : children}
      </div>
      {(effectiveOverflowing || footer) && (
        <div className={element('footer')}>
          {footer?.(renderProps)}
          {effectiveOverflowing && (
            <button
              type="button"
              className={element('toggle')}
              data-testid={testId ? `${testId}-toggle` : 'collapsible-content-toggle'}
              onClick={(): void => setIsExpanded((prev) => !prev)}
            >
              {isExpanded ? collapseLabel : expandLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CollapsibleContent;
